// AC GUIDE 1: INVERTER VS NON-INVERTER (Oct 2026). /blog/inverter-vs-non-inverter-ac
//
// Rebuilt 20 Oct 2026 to GUIDES_PLAYBOOK.md section 3 (Saheb, 19 Oct 2026:
// "Questions need to be answered first with some animation... no clear
// directional answer... explain like to an 8th standard kid").
//
// THE ANSWER IS SAHEB'S ADVICE (19 Oct 2026, "I have told you the same
// information 1000 times"): an inverter for a bedroom used 6 or more hours a
// day, not on the top floor and not an open space; a non-inverter for a few
// hours a day, open or large spaces, a top-floor room, an AC smaller than the
// room used for spot cooling, or low maintenance. The room model
// (data/roomDay.js) only shows WHY: an inverter saves when it can slow down,
// and in a hall or a top-floor room it can't.
//
// SOURCES. Every fact comes from Daikin, the Government of India or Citi
// Cool's own experience. Each claim carries a source key, shown as a footnote.
//
// ONE FILE, TWO READERS. The page (pages/InverterGuide.jsx) and the copy
// Google reads (scripts/guide-pages.js) both build from inverterContent(), so
// they say the same thing. No imports: prerender.js loads this file by
// stripping `export`. **double stars** mean bold.
//
// NO PRICES HERE. pickGuideModels() picks the ACs on every build from the
// catalogue, prices and stock, in stock first, so a sell-out moves the page.

export const GUIDE_SLUG = 'blog/inverter-vs-non-inverter-ac';
export const GUIDE_PUBLISHED = '2026-10-17';
export const GUIDE_UPDATED = '2026-10-20';

export const GUIDE_SEO = {
  title: 'Inverter vs Non-Inverter AC: Which One Should You Buy? | Citi Cool',
  meta: 'Inverter or non-inverter AC? An inverter for a bedroom used 6+ hours a day. A non-inverter for short use, a top floor, a hall or cheap repairs. See why.',
  eyebrow: 'AC Guide · Buying',
  h1: 'Inverter vs non-inverter AC: which one should you buy?',
  crumb: 'Inverter vs non-inverter',
  readMins: 4,
};

// What our service team charges and sees, Oct 2026 (Saheb). A new inverter PCB
// costs "much more" than a repair; no figure until Saheb gives one.
export const REPAIRS = { pcb: 3500, capacitor: 1500 };

export const RATE_DEFAULT = 8;        // ₹ a unit for homes
export const MONTHS_DEFAULT = 8;      // Delhi NCR runs ACs 8 months a year
export const BEE_HOURS = 1600;        // BEE rates every AC over a 1,600-hour year
export const SIZE = '1.5';

// The two rooms of the animation, run through the room model by the caller
// (roomComparison in data/roomDay.js) with the non-inverter and the 3 Star.
export const TWO_ROOMS = [
  { key: 'bed', label: 'Bedroom at night', short: 'Bedroom', note: '12 × 12 ft, middle floor, 10 pm to 6 am',
    room: { city: 'delhi', kind: 'bedroom', size: '12x12', floor: 'mid', sun: 'W', setC: 24, day: 'typical', schedule: 'night' } },
  { key: 'hall', label: 'Top-floor hall, afternoon', short: 'Hall', note: '14 × 16 ft, top floor, 12 to 6 pm',
    room: { city: 'delhi', kind: 'living', size: '14x16', floor: 'top', sun: 'W', setC: 24, day: 'typical', schedule: 'afternoon' } },
];

// ─── sources ──────────────────────────────────────────────────────────────

export const SOURCES = [
  { key: 'saheb', label: 'Our showroom\'s buying advice: inverter for long-use bedrooms, non-inverter for short use, top floors, open areas and low upkeep (October 2026)', by: 'Citi Cool', href: null },
  { key: 'daikin-inverter', label: 'Inverter Technology', by: 'Daikin India', href: 'https://daikinindia.com/inverter-technology-0' },
  { key: 'daikin-label', label: 'BEE star label figures from the Daikin RA Master Catalogue 2026, shown on each product page', by: 'Daikin', href: '/daikin-1-5-ton-ac-price' },
  { key: 'calc', label: "Our room model: Daikin's method for how much heat gets into a room, hour by hour, on IMD weather for Delhi, as in our AC calculator", by: 'Citi Cool engineering', href: '/ac-calculator' },
  { key: 'derate', label: "Daikin's full-cooling outside temperature for each series, as used in our AC calculator", by: 'Daikin · Citi Cool engineering', href: '/ac-calculator' },
  { key: 'service', label: 'Repair costs and breakdowns seen by our own service team, October 2026', by: 'Citi Cool', href: null },
  { key: 'daikin-warranty', label: 'Product Warranty', by: 'Daikin India', href: 'https://www.daikinindia.com/node/34556' },
  { key: 'daikin-pcb', label: 'Top 10 Reasons to Buy Daikin 5 Star Inverter AC', by: 'Daikin India', href: 'https://daikinindia.com/node/35305' },
  { key: 'prices', label: 'Daikin 1.5 ton AC prices and stock today', by: 'citicool.in', href: '/daikin-1-5-ton-ac-price' },
];

// The sources a content object actually cites, in list order. Every guide
// uses this, so the Sources list never carries an unused entry.
export function usedSources(all, c) {
  const keys = new Set();
  const walk = (v) => {
    if (!v) return;
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (typeof v === 'object') {
      if (Array.isArray(v.src)) v.src.forEach((k) => keys.add(k));
      Object.values(v).forEach(walk);
    }
  };
  walk(c);
  return all.filter((s) => keys.has(s.key));
}

// ─── the ACs ──────────────────────────────────────────────────────────────

// Must agree with onlineKit() in lib/listing.js: the without-kit unit, unless
// it has none in stock and the with-kit unit has.
const onlineKit = (m, stock) => {
  const v = m.variants;
  if (!v.withoutKit) return 'withKit';
  const s = (stock || {})[m.id] || {};
  return v.withKit && !(s.withoutKit > 0) && s.withKit > 0 ? 'withKit' : 'withoutKit';
};

// Must agree with typeOf() in lib/acRecommend.js.
const typeOf = (m) => {
  if (!m.inverter) return 'fixed';
  if (m.series === 'FTHT') return 'heat';
  return m.stars >= 5 ? 'five' : 'three';
};

// "GTQ50XV16UCA" -> "GTQ50"
const shortCode = (idu) => (String(idu).match(/^[A-Z]+\d+/) || [idu])[0];

export const TYPE_NAME = { fixed: 'Non-inverter', three: '3 Star inverter', five: '5 Star inverter' };

// `size` is a catalogue soldAs label ('1', '1.5', '1.8' ...). A size with no
// non-inverter (1.8 ton and up) returns fixed: null.
export function pickGuideModels({ catalogue, prices, stock, series, size = SIZE }) {
  const pool = catalogue
    .filter((m) => m.soldAs === size)
    .map((m) => {
      const kit = onlineKit(m, stock);
      const price = (prices[m.id] || {})[kit];
      const left = (stock[m.id] || {})[kit];
      return {
        model: m,
        kit,
        price,
        code: shortCode(m.variants[kit].idu),
        type: typeOf(m),
        inStock: (left || 0) > 0,
        soldOut: left === 0,
        full: (series[m.series] && series[m.series].heat && series[m.series].heat.full) || 43,
      };
    })
    .filter((x) => typeof x.price === 'number' && x.price > 0);

  const best = (type) => {
    const ofType = pool.filter((x) => x.type === type);
    const buyable = ofType.filter((x) => !x.soldOut)
      .sort((a, b) => (Number(b.inStock) - Number(a.inStock)) || (a.price - b.price));
    return buyable[0] || ofType.sort((a, b) => a.price - b.price)[0] || null;
  };

  const out = { fixed: best('fixed'), three: best('three'), five: best('five') };
  out.list = [out.fixed, out.three, out.five].filter(Boolean);
  return out;
}

export const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
// "about ₹600": rounded to the nearest hundred.
export const inr100 = (n) => `₹${(Math.round(n / 100) * 100).toLocaleString('en-IN')}`;
const pct = (a, b) => Math.round(((a - b) / a) * 100);

// Electricity a year at `hours` a day, from the BEE label: units × hours ÷ 1,600.
export const yearCost = (x, hours) => (x.model.annualKwh * hours * 30 * MONTHS_DEFAULT * RATE_DEFAULT) / BEE_HOURS;

// ─── the two rooms ────────────────────────────────────────────────────────

// cmps: roomComparison() results for TWO_ROOMS, in order, each run with
// [fixed, three]. Returns per room: rupees for the hours of use, hours the AC
// can't keep up, and the warmest the room gets.
export function twoRooms(cmps, fixed, three) {
  return TWO_ROOMS.map((r, i) => {
    const cmp = cmps[i];
    const row = (x) => cmp.rows.find((y) => y.ac.model.id === x.model.id);
    const f = row(fixed); const v = row(three);
    return {
      ...r,
      hours: cmp.hours.length,
      codes: { fixed: fixed.code, inv: three.code },
      fixed: { rupees: f.units * RATE_DEFAULT, flat: f.flatHours, warmest: f.warmest, share: Math.min(1, f.units / (f.pFull * cmp.hours.length)) },
      inv: { rupees: v.units * RATE_DEFAULT, flat: v.flatHours, warmest: v.warmest, share: Math.min(1, v.units / (v.pFull * cmp.hours.length)) },
    };
  });
}

// ─── the page ─────────────────────────────────────────────────────────────

// g: pickGuideModels(); rooms: twoRooms().
export function inverterContent(g, rooms) {
  const F = g.fixed; const T = g.three; const V = g.five;
  const bed = rooms[0]; const hall = rooms[1];
  const bedSave = pct(bed.fixed.rupees, bed.inv.rupees);
  const hallWarm = Math.round(Math.max(hall.fixed.warmest, hall.inv.warmest));
  const save4 = yearCost(F, 4) - yearCost(T, 4);
  const hallSaves = hall.inv.rupees < hall.fixed.rupees - 0.5;

  return {
    answer: {
      text: '**Choose an inverter AC for a bedroom you use 6 or more hours a day. Choose a non-inverter for short use, a top-floor room or an open hall.** It also suits a small AC cooling one spot, and cheap repairs.',
      src: ['saheb'],
    },
    because: [
      { text: 'An inverter saves power only when it can slow down.', src: ['daikin-inverter'] },
      { text: 'On a top floor or in a big hall, it runs at full power. Then it saves nothing.', src: ['calc'] },
      { text: `Inverter repairs cost more: about ${inr(REPAIRS.pcb)} for its circuit board. A non-inverter's capacitor costs about ${inr(REPAIRS.capacitor)}.`, src: ['service'] },
    ],
    watch: {
      title: 'Watch: inverter and non-inverter AC in a bedroom and a hall',
      captions: [
        { at: 0, text: 'Two hot rooms. Each gets a non-inverter and an inverter AC. All are set to 24°C.' },
        { at: 0.3, text: 'The bedroom cools to 24°C. The inverter slows down. The non-inverter keeps switching on and off.' },
        { at: 0.55, text: `The hall never gets to 24°C. It stays near ${hallWarm}°C. Both ACs run at full power all afternoon.` },
        { at: 0.8, text: hallSaves
          ? `So the inverter saves in the bedroom, about ${bedSave}% a night. In the hall it saves almost nothing.`
          : `So the inverter saves in the bedroom, about ${bedSave}% a night. In the hall it saves nothing. Here it even costs more.` },
      ],
      note: `Two 1.5 ton Daikin ACs: the ${bed.codes.fixed} non-inverter and the ${bed.codes.inv} inverter. Delhi, on a hot day, at ${inr(RATE_DEFAULT)} a unit.`,
      noteSrc: ['calc', 'daikin-label'],
    },
    table: {
      title: 'Inverter or non-inverter: which is right for your room?',
      head: ['Your room', 'Choose'],
      rows: [
        ['Bedroom, 6 or more hours a day', 'Inverter'],
        ['Room used a few hours (guest room, study)', 'Non-inverter'],
        ['Top-floor room', 'Non-inverter'],
        ['Open area (hall, living-dining, shop)', 'Non-inverter'],
        ['AC smaller than the room, cooling one spot', 'Non-inverter'],
        ['You want low upkeep and cheap repairs', 'Non-inverter'],
      ],
      note: V && T && V.full > T.full
        ? `Want an inverter on a top floor anyway? Take a **5 Star**. It cools fully up to ${V.full}°C outside. The 3 Star does up to ${T.full}°C.`
        : 'Want an inverter on a top floor anyway? Take a 5 Star.',
      src: ['saheb', 'derate'],
    },
    sides: [
      {
        key: 'inverter',
        title: 'Inverter AC: pros and cons',
        good: [
          { text: 'It saves power in a room it can cool. It slows down instead of switching off.', src: ['daikin-inverter'] },
          { text: 'The room stays at a steady temperature.', src: ['daikin-inverter'] },
          ...(V ? [{ text: `It uses fewer units. On its star label, the ${V.code} 5 Star uses ${pct(F.model.annualKwh, V.model.annualKwh)}% less than the ${F.code}.`, src: ['daikin-label'] }] : []),
          { text: 'Daikin gives its compressor a 10-year warranty.', src: ['daikin-warranty'] },
        ],
        bad: [
          ...(T.price > F.price ? [{ text: `It costs more to buy. Today the ${T.code} costs ${inr(T.price - F.price)} more than the ${F.code}.`, src: ['prices'] }] : []),
          { text: 'On a top floor or in a hall, it saves nothing.', src: ['calc'] },
          { text: `Its circuit board repair costs about ${inr(REPAIRS.pcb)} after the warranty. A new board costs much more.`, src: ['service'] },
        ],
      },
      {
        key: 'fixed',
        title: 'Non-inverter AC: pros and cons',
        good: [
          { text: 'It costs less to buy.', src: ['prices'] },
          { text: 'It is very sturdy. It rarely breaks down.', src: ['service'] },
          { text: `Its usual repair is a capacitor. That costs about ${inr(REPAIRS.capacitor)}.`, src: ['service'] },
          { text: 'In a hot hall or a top-floor room, it does as well as an inverter.', src: ['calc'] },
        ],
        bad: [
          { text: 'It uses more units in a room it can cool fully.', src: ['daikin-label'] },
          { text: 'It switches fully on and off. So the room warms and cools a little.', src: ['daikin-inverter'] },
          { text: 'Daikin gives its compressor 5 years of warranty, not 10.', src: ['daikin-warranty'] },
        ],
      },
    ],
    parts: [
      {
        id: 'why',
        q: 'Why does an inverter AC save electricity?',
        bullets: [
          { text: 'Think of a fan regulator. An inverter AC can turn its speed down.', src: ['daikin-inverter'] },
          { text: 'A non-inverter has one speed. It runs fully, then it switches off.', src: ['daikin-inverter'] },
          { text: 'Once the room is cool, the inverter needs little power to keep it cool.', src: ['daikin-inverter'] },
          { text: 'Watch both at work in our [inverter and non-inverter explainer](/blog/difference-between-inverter-and-non-inverter-ac).', src: [] },
        ],
      },
      {
        id: 'top-floor',
        q: 'Is an inverter AC good for a top-floor room?',
        bullets: [
          { text: 'Usually not. The hot roof heats the room all afternoon.', src: ['calc'] },
          { text: 'The AC runs at full power for hours. So it never slows down.', src: ['calc'] },
          { text: 'Then the inverter saves nothing. And its repairs cost more.', src: ['saheb', 'service'] },
          ...(V && T && V.full > T.full ? [{ text: `Still want one? Take a [5 Star](/blog/3-star-vs-5-star-ac). It cools fully up to ${V.full}°C outside.`, src: ['saheb', 'derate'] }] : []),
        ],
      },
      {
        id: 'hall',
        q: 'Which AC is best for a hall or living room?',
        bullets: [
          { text: 'A big open room needs a lot of cooling.', src: ['calc'] },
          { text: `In our test hall, the room stayed near ${hallWarm}°C with either AC.`, src: ['calc'] },
          { text: 'Both ran at full power. So a non-inverter did the same job for less money.', src: ['calc', 'saheb'] },
        ],
      },
      {
        id: 'light-use',
        q: 'Is an inverter AC worth it for 4 hours a day?',
        bullets: [
          { text: `No. At 4 hours a day, it saves about ${inr100(save4)} a year.`, src: ['daikin-label'] },
          { text: `One circuit board repair costs about ${inr(REPAIRS.pcb)}. That wipes out years of saving.`, src: ['service'] },
          { text: 'So for short use, buy a non-inverter.', src: ['saheb'] },
        ],
      },
    ],
    faq: [
      {
        q: 'Is an inverter AC better than a non-inverter?',
        a: 'For a bedroom used 6 or more hours a day, yes. For short use, a top floor or a hall, a non-inverter is the better buy.',
        src: ['saheb'],
      },
      {
        q: 'Does an inverter AC really save electricity?',
        a: `Yes, in a room it can cool fully. In our bedroom test, the inverter used about ${bedSave}% less at night. See [how many units an AC uses](/blog/ac-power-consumption-units-per-hour).`,
        src: ['calc'],
      },
      {
        q: 'Which AC is best for a bedroom?',
        a: 'An inverter, if you use it 6 or more hours a day. Pick a non-inverter if the bedroom is on the top floor. Then check [what size AC you need](/blog/what-size-ac-do-i-need).',
        src: ['saheb'],
      },
      {
        q: 'Do I need a stabiliser for an inverter AC?',
        a: 'Daikin says you do not need a separate stabiliser with its inverter ACs.',
        src: ['daikin-pcb'],
      },
      {
        q: 'What is the warranty on inverter and non-inverter ACs?',
        a: 'An inverter gets 1 year on the AC and 5 on its circuit board. Its compressor gets 10. A non-inverter gets 1 year on the AC and 5 on the compressor.',
        src: ['daikin-warranty'],
      },
      {
        q: 'Does an inverter AC run on a home inverter?',
        a: 'No. The name has nothing to do with power backup. It is the part that changes how fast the AC runs.',
        src: ['daikin-inverter'],
      },
    ],
  };
}
